/*
 * ReservierungsPositionen.tsx - Resevierungspositionen darstellen.
 *
 * Created by Dr. Maximillian Dornseif 2023-10-23 in huWaWi3 33.2.1
 * Copyright (c) 2023 HUDORA GmbH
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { LadeChecker, LinkRender, VerfuegbarRender } from '@hudora/hd-react-components';
import { ValueRender } from '@hudora/react-jsonschema-valuerender';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { DocumentNode } from 'graphql';
import React from 'react';
import { gql, useQuery } from 'urql';

import { PREIS_FRAGMENT, RESERVIERUNGSPOSITION_FRAGMENT } from '../../fragments';
import { useAuditPanel } from '../../lib/hooks/useAuditPanel';
import { useEditPanel3 } from '../../lib/hooks/useEditPanel3';
import { useRohdatenPanel3 } from '../../lib/hooks/useRohdatenPanel3';

// Declaring type of props - see "Typing Component Props" for more examples
type DisplayPositionenProps = {
  reservierung: Record<string, any>; // Auftrag oder Resevierung
  nr: string;
  gqlQuery: DocumentNode;
}; /* use `interface` if exporting so that consumers can extend */

export function ReservierungsPositionen({ reservierung, nr, gqlQuery }: DisplayPositionenProps) {
  const [result] = useQuery({
    query: gqlQuery,
    variables: { nr },
  });
  const { data, fetching, error } = result;
  const { auditPanel, openAuditPanel } = useAuditPanel();
  const [, rohdatenPanel, openRohdatenPanel] = useRohdatenPanel3({
    schemaName: 'HwReservierungsPosition',
    entity: undefined,
  });
  const [commandBarItem, editPanel, openEditPanel] = useEditPanel3({
    schemaName: 'HwReservierungsPosition',
    mutation: POS_PATCH_MUTATION,
    entity: undefined,
  });

  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Positionen`}>
      <section>
        <h2>
          Positionen
          {/* <button class="btn btn-primary btn-xs"
           data-toggle="modal" data-target="#position-zufuegen-modal"
           >Zufügen</button> */}
        </h2>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell title="bestellt / geliefert / offen">
                  b&thinsp;/&thinsp;g&thinsp;/&thinsp;o
                </TableCell>
                <TableCell>Item№</TableCell>
                <TableCell>Artikel</TableCell>
                <TableCell align="right">Resevierungspreis</TableCell>
                <TableCell align="right">FH</TableCell>
                <TableCell>Verfügbar</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.entity?.positionen?.map((pos) => (
                <TableRow key={pos.posnr}>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {pos.menge_bestellt} / {pos.menge_geliefert} / <b>{pos.menge_offen}</b>
                  </TableCell>

                  <TableCell>
                    <LinkRender value={pos.bartnr} />
                  </TableCell>
                  <TableCell>
                    {pos?.item?.name}
                    {/* {% if pos.menge_offen %}
                  <span class="ArtikelZukunftApp" data-artnr="{{ pos.artnr }}"></span>
                  {% endif %} */}
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }} align="right">
                    <ValueRender value={pos.preis} format="eurocent" />
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }} align="right">
                    <ItemFhPreis itemNr={pos.bartnr} />
                  </TableCell>
                  <TableCell>
                    {!pos.erledigt ? (
                      <VerfuegbarRender
                        itemNr={pos.bartnr}
                        kundenNr={reservierung?.kunde?.designator}
                        auftragsNr={reservierung?.designator}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      iconProps={{ iconName: 'Edit' }}
                      title="Rohdaten"
                      ariaLabel="Rohdaten"
                      onClick={() => openEditPanel(pos)}
                    />
                    <IconButton
                      iconProps={{ iconName: 'PageData' }}
                      title="Rohdaten"
                      ariaLabel="Rohdaten"
                      onClick={() => openRohdatenPanel(pos)}
                    />
                    {/* <IconButton
                      iconProps={{ iconName: 'Memo' }}
                      title="Audit"
                      ariaLabel="Audit"
                      onClick={() => openAuditPanel(pos?.designator, pos)}
                    /> */}
                  </TableCell>
                </TableRow>
              ))}
              {/* {% if pos.infotext_kunde %}
             <tr><th>Kundenreferenz:</th><td colspan="4">{{ pos.infotext_kunde|e }}</td></tr>
            {% endif %} */}
              {/* {% else %}
             <tr><td><strong>keine Positionen vorhanden</strong></td></tr> */}
            </TableBody>
          </Table>
        </TableContainer>
        {rohdatenPanel}
        {editPanel}
        {auditPanel}
      </section>
    </LadeChecker>
  );
}

const POS_PATCH_MUTATION = gql`
  mutation ResevierungsPositionPatch(
    $designator: ID!
    $input: HwReservierungsPositionPatchInput!
    $oldRev: ID!
  ) {
    patchInfo: hwReservierungsPositionPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...resevierungspositionFragment
      }
      new {
        ...resevierungspositionFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${RESERVIERUNGSPOSITION_FRAGMENT}
`;

function ItemFhPreis({ itemNr }) {
  const [result] = useQuery({
    query: PREIS_QUERY,
    pause: !itemNr || itemNr.length < 5,
    variables: { nr: itemNr },
  });
  const { data, fetching } = result;

  const fhPreis = data?.h3Item?.preis?.fh;

  return (
    <LadeChecker loading={fetching}>
      {fhPreis ? (
        <React.Fragment>
          <ValueRender value={fhPreis} format="eurocent" />
        </React.Fragment>
      ) : (
        <div style={{ verticalAlign: 'middle' }}>-</div>
      )}
    </LadeChecker>
  );
}

const PREIS_QUERY = gql`
  query PreisLesen($nr: ID!) {
    h3Item(designator: $nr) {
      id
      preis: preisResolved {
        ...preisLFragment
      }
    }
  }
  ${PREIS_FRAGMENT}
`;
